import React, { createContext, useContext, useEffect, useState } from 'react'
import { AppConfig } from '@/model/entities/app-config.interface'
import { ConfigDataService } from '@/services/ConfigDataService'

interface ConfigContextType {
    config: AppConfig | null
    loading: boolean
    error: Error | null
}

const ConfigContext = createContext<ConfigContextType>({
    config: null,
    loading: true,
    error: null,
})

export const useConfigContext = () => useContext(ConfigContext)

interface ConfigProviderProps {
    children: React.ReactNode
    fallback?: React.ReactNode
}

const configService = new ConfigDataService()
let cachedConfig: AppConfig | null = null

export const ConfigProvider: React.FC<ConfigProviderProps> = ({
    children,
    fallback
}) => {
    const [config, setConfig] = useState<AppConfig | null>(cachedConfig)
    const [loading, setLoading] = useState<boolean>(!cachedConfig)
    const [error, setError] = useState<Error | null>(null)

    useEffect(() => {
        if (cachedConfig) {
            setConfig(cachedConfig)
            setLoading(false)
            return
        }

        configService
            .getConfigData()
            .then((data: AppConfig) => {
                cachedConfig = data
                setConfig(data)
                setLoading(false)
            })
            .catch((err: Error) => {
                setError(err)
                setLoading(false)
            })
    }, [])

    // Block rendering until config is loaded to prevent flickering
    if (loading) {
        return <>{fallback || null}</>
    }

    return (
        <ConfigContext.Provider value={{ config, loading, error }}>
            {children}
        </ConfigContext.Provider>
    )
}

// Export the cached config getter for synchronous access after initialization
export const getConfig = (): AppConfig | null => cachedConfig
